import { db } from './config';
import { collection, query, where, getDocs, updateDoc, deleteDoc, doc, addDoc, serverTimestamp, or, orderBy, limit, startAfter } from 'firebase/firestore';
import { getAuthHeaders } from './authHeaders';

// Cloud Functions base URL
const CLOUD_FUNCTIONS_BASE_URL = process.env.REACT_APP_PAYMENT_API || 'http://localhost:5001/card-market-cloud-functions/us-central1/api';

const INCOMES_COLLECTION = 'incomes';
const SETTLEMENTS_COLLECTION = 'settlements';

// Get all unsettled incomes (pending)
export const getUnsettledIncomes = async () => {
    const q = query(
        collection(db, INCOMES_COLLECTION),
        where('settled', '==', false)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
};

// Get withdrawable incomes (older than 5 days)
export const getWithdrawableIncomeAmount = async () => {
    const fiveDaysAgo = new Date();
    fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5);

    const q = query(
        collection(db, INCOMES_COLLECTION),
        where('settled', '==', false),
        where('created_at', '<=', fiveDaysAgo) // Assuming 'created_at' is the field storing the income creation date
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data()})).reduce((total, income) => total + (income.amount || 0), 0);
};
export const getPlatformBalance = async (userId) => {
    try {
        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/platform-balance`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(await getAuthHeaders())
            },
            body: JSON.stringify({
                userId
            })
        });
        if(response.status === 200){
            const data = await response.json();
            return data;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch platform balance');
    } catch (error) {
        console.error('Error fetching platform balance:', error);
        throw error;
    }
}
export const getIncomeIdsForSettlement = async (userId) => {
    try {
        //Test Only
        // const snapshot = await getDocs(query(collection(db, INCOMES_COLLECTION), where('settled', '==', true)));
        // snapshot.docs.forEach(doc => {
        //     updateDoc(doc.ref, { settled: false, settled_at: null, settle_by: null });
        // });
        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/get-income-ids-for-settlement`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(await getAuthHeaders())
            },
            body: JSON.stringify({
                userId
            })
        });
        if(response.status === 200){
            const data = await response.json();
            return data;
        }
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch income IDs for settlement');
    } catch (error) {
        console.error('Error fetching income IDs for settlement:', error);
        throw error;
    }
}

export const settleIncomes = async (incomeIds, userId, description) => {
    try { 
        const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/settlement`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(await getAuthHeaders())
            },
            body: JSON.stringify({
                incomeIds,
                userId,
                description
            })
        });
        if(response.status !== 200){
            const errorData = await response.json();
            throw new Error(errorData.error || 'Failed to settle incomes');
        }
        const data = await response.json();
        return data;
    }
    catch (error) {
        console.error('Error settling incomes:', error);
        throw error;
    }
}

export const getAllIncomes = async (page = 1, pageSize = 8) => {
    console.log('Fetching all incomes');
    const q = query(
        collection(db, INCOMES_COLLECTION),
        orderBy('created_at', 'desc')
    );
    const snapshot = await getDocs(q);
    const allIncomes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    console.log('Fetched incomes:', allIncomes);

    return { allIncomes };
}

export const getIncomeStatistics = async () => {
    try {
        const snapshot = await getDocs(collection(db, INCOMES_COLLECTION));

        const totalIncomeQuery = query(
            collection(db, INCOMES_COLLECTION),
        );
        const settledIncomeQuery = query(
            collection(db, INCOMES_COLLECTION),
            where('settled', '==', true)
        );
        const settleableIncomeQuery = query(
            collection(db, INCOMES_COLLECTION),
            where('settled', '==', false),
            where('created_at', '<=', new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)) // Assuming settleable if older than 5 days
        );
        const settleableIncomeQuery2 = query(
            collection(db, INCOMES_COLLECTION),
            where('settled', '==', null),
            where('created_at', '<=', new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)) // Assuming settleable if older than 5 days
        );

        const [totalIncomeSnapshot, settledIncomeSnapshot, settleableIncomeSnapshot, settleableIncomeSnapshot2] = await Promise.all([
            getDocs(totalIncomeQuery),
            getDocs(settledIncomeQuery),
            getDocs(settleableIncomeQuery),
            getDocs(settleableIncomeQuery2)
        ]);

        const totalIncome = totalIncomeSnapshot.docs.reduce((total, income) => total + (income.data().amount || 0), 0);
        const settledIncome = settledIncomeSnapshot.docs.reduce((total, income) => total + (income.data().amount || 0), 0);
        const settleableIncome = 
            settleableIncomeSnapshot.docs.reduce((total, income) => total + (income.data().amount || 0), 0) +
            settleableIncomeSnapshot2.docs.reduce((total, income) => total + (income.data().amount || 0), 0);
        const pendingIncome = totalIncome - settledIncome - settleableIncome;

        return {
            totalIncome,
            settledIncome,
            settleableIncome,
            pendingIncome,
            unsettledIncome: pendingIncome + settleableIncome
        };
    } catch (error) {
        console.error('Error getting income statistics:', error);
        throw error;
    }
}